const imageMimeTypeByExtension: Record<string, string> = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  gif: 'image/gif',
  bmp: 'image/bmp',
  webp: 'image/webp',
};

export function fileExtensionFromValue(value?: string | null): string {
  if (!value) return '';
  const cleanValue = value.split(/[?#]/)[0] ?? value;
  const fileName = cleanValue.split('/').filter(Boolean).pop() ?? cleanValue;
  return fileName.includes('.') ? fileName.split('.').pop()?.toLowerCase() ?? '' : '';
}

export function normalizePreviewMimeType(value?: string | null): string | undefined {
  return value?.split(';')[0]?.trim().toLowerCase() || undefined;
}

export function previewMimeTypeFromExtension(extension: string): string | undefined {
  if (extension === 'pdf') return 'application/pdf';
  return imageMimeTypeByExtension[extension];
}

export function previewMimeTypeFromValues(...values: Array<string | null | undefined>): string | undefined {
  for (const value of values) {
    const mimeType = previewMimeTypeFromExtension(fileExtensionFromValue(value));
    if (mimeType) return mimeType;
  }

  return undefined;
}

export function previewMimeTypeFromBytes(data?: ArrayBuffer): string | undefined {
  if (!data) return undefined;

  const bytes = new Uint8Array(data.slice(0, 16));
  if (bytes.length >= 4 && bytes[0] === 0x25 && bytes[1] === 0x50 && bytes[2] === 0x44 && bytes[3] === 0x46) {
    return 'application/pdf';
  }
  if (bytes.length >= 3 && bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) {
    return 'image/jpeg';
  }
  if (
    bytes.length >= 8 &&
    bytes[0] === 0x89 &&
    bytes[1] === 0x50 &&
    bytes[2] === 0x4e &&
    bytes[3] === 0x47 &&
    bytes[4] === 0x0d &&
    bytes[5] === 0x0a &&
    bytes[6] === 0x1a &&
    bytes[7] === 0x0a
  ) {
    return 'image/png';
  }
  if (
    bytes.length >= 6 &&
    bytes[0] === 0x47 &&
    bytes[1] === 0x49 &&
    bytes[2] === 0x46 &&
    bytes[3] === 0x38
  ) {
    return 'image/gif';
  }
  if (bytes.length >= 2 && bytes[0] === 0x42 && bytes[1] === 0x4d) {
    return 'image/bmp';
  }
  if (
    bytes.length >= 12 &&
    bytes[0] === 0x52 &&
    bytes[1] === 0x49 &&
    bytes[2] === 0x46 &&
    bytes[3] === 0x46 &&
    bytes[8] === 0x57 &&
    bytes[9] === 0x45 &&
    bytes[10] === 0x42 &&
    bytes[11] === 0x50
  ) {
    return 'image/webp';
  }

  return undefined;
}

export function isPdfMimeType(mimeType?: string | null): boolean {
  const normalized = normalizePreviewMimeType(mimeType);
  return normalized === 'application/pdf' || normalized === 'application/x-pdf';
}

export function isImageMimeType(mimeType?: string | null): boolean {
  return normalizePreviewMimeType(mimeType)?.startsWith('image/') ?? false;
}

export function resolvePreviewMimeType({
  data,
  mimeType,
  names = [],
}: {
  data?: ArrayBuffer;
  mimeType?: string | null;
  names?: Array<string | null | undefined>;
}): string | undefined {
  const normalizedMimeType = normalizePreviewMimeType(mimeType);
  if (
    normalizedMimeType &&
    normalizedMimeType !== 'application/octet-stream' &&
    normalizedMimeType !== 'binary/octet-stream'
  ) {
    return normalizedMimeType;
  }

  return previewMimeTypeFromBytes(data) ?? previewMimeTypeFromValues(...names);
}
